import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * StatusPill — a state with semantics, never colour alone.
 *
 * Every tone pairs a token colour with a distinct glyph shape and a text
 * label, so the state survives greyscale, colour-blindness and the
 * high-contrast theme:
 *
 *   healthy   ● emerald  — confirmed healthy only (never "probably fine")
 *   attention ▲ brass    — needs a human soon; not a failure
 *   danger    ◆ red      — failure, refusal, breach; red is reserved for this
 *   held      ‖ steel    — blocked by a rule until its cause is fixed; no one
 *                          may act on it (e.g. a held enrollment request)
 *   pending   ○ graphite — waiting on someone or something; `pulse` animates
 *                          the glyph unless the user prefers reduced motion
 *   neutral   ○ graphite — a plain state with no judgement (e.g. "Stock")
 *
 * `appearance="inline"` drops the box for dense table cells.
 */

export type StatusTone = 'healthy' | 'attention' | 'danger' | 'held' | 'pending' | 'neutral';

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: StatusTone;
  appearance?: 'pill' | 'inline';
  /** Animate the pending glyph (ignored for other tones and reduced motion). */
  pulse?: boolean;
}

const toneClasses: Record<StatusTone, { pill: string; glyph: string }> = {
  healthy: {
    pill: 'border-[color-mix(in_oklab,var(--color-success)_45%,transparent)] bg-[var(--color-success-tint)]',
    glyph: 'text-[var(--color-success)]',
  },
  attention: {
    pill: 'border-[color-mix(in_oklab,var(--color-warning)_50%,transparent)] bg-[var(--color-warning-tint)]',
    glyph: 'text-[var(--color-warning)]',
  },
  danger: {
    pill: 'border-[color-mix(in_oklab,var(--color-danger)_50%,transparent)] bg-[var(--color-danger-tint)]',
    glyph: 'text-[var(--color-danger)]',
  },
  held: {
    pill: 'border-[color-mix(in_oklab,var(--color-accent)_50%,transparent)] bg-[var(--color-accent-tint)]',
    glyph: 'text-[var(--color-accent)]',
  },
  pending: {
    pill: 'border-[var(--color-hairline-strong)] bg-[var(--color-surface-2)]',
    glyph: 'text-[var(--color-foreground-3)]',
  },
  neutral: {
    pill: 'border-[var(--color-hairline)] bg-transparent',
    glyph: 'text-[var(--color-foreground-3)]',
  },
};

function StatusGlyph({ tone, pulse }: { tone: StatusTone; pulse: boolean }): React.JSX.Element {
  const common = {
    'aria-hidden': true,
    focusable: false,
    viewBox: '0 0 10 10',
    className: cn(
      'size-2.5 shrink-0',
      toneClasses[tone].glyph,
      pulse && tone === 'pending' && 'motion-safe:animate-pulse',
    ),
  } as const;
  switch (tone) {
    case 'healthy':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="4" fill="currentColor" />
        </svg>
      );
    case 'attention':
      return (
        <svg {...common}>
          <path d="M5 1 9.2 8.8H.8Z" fill="currentColor" />
        </svg>
      );
    case 'danger':
      return (
        <svg {...common}>
          <path d="M5 .6 9.4 5 5 9.4.6 5Z" fill="currentColor" />
        </svg>
      );
    case 'held':
      return (
        <svg {...common}>
          <rect x="1.5" y="1" width="2.4" height="8" rx=".4" fill="currentColor" />
          <rect x="6.1" y="1" width="2.4" height="8" rx=".4" fill="currentColor" />
        </svg>
      );
    case 'pending':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="5" cy="5" r="1.4" fill="currentColor" />
        </svg>
      );
    case 'neutral':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
  }
}

const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ tone, appearance = 'pill', pulse = false, className, children, ...props }, ref) => (
    <span
      ref={ref}
      data-slot="status-pill"
      data-tone={tone}
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap text-xs font-medium leading-none text-[var(--color-foreground)]',
        appearance === 'pill' && ['rounded-sm border px-1.5 py-1', toneClasses[tone].pill],
        className,
      )}
      {...props}
    >
      <StatusGlyph tone={tone} pulse={pulse} />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  ),
);
StatusPill.displayName = 'StatusPill';

export { StatusPill };
